import React, { useMemo } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { css } from 'styled-components';

import { MissionCardComplexeStatus, MissionCardComplexeStatuses } from '../../types/components';
import { MissionOperationTypes, MissionOperationType } from '../../types/state/missions';
import { MissionEnrollmentButtonLabelClassName } from '../MissionEnrollmentButton/MissionEnrollmentButton';
import MissionEnrollmentButtonContainer from '../MissionEnrollmentButton/MissionEnrollmentButtonContainer';
import { MissionProgressLabelClassName } from '../MissionProgressBar/MissionProgressBar';
import MissionProgressBarContainer from '../MissionProgressBar/MissionProgressBarContainer';

const CTA_TRANSITION_DURATION = 3000; // ms
const ENROLL_BUTTON_TRANSITION_DURATION = CTA_TRANSITION_DURATION * 0.85; // ms

type WrapperType = {
  $large: boolean;
};

const Wrapper = styled.div<WrapperType>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $large }) =>
    $large
      ? css`
          width: 100%;
        `
      : css`
          min-width: 8em;
        `}

  flex-shrink: 0;
`;

type EnrollmentButtonWrapperType = {
  $shouldTransition: boolean;
  $missionStatus: MissionCardComplexeStatus;
  $show: boolean;
};

const EnrollmentButtonWrapper = styled.div<EnrollmentButtonWrapperType>`
  width: 100%;

  &.mission-enrollment-button-transitions-enter {
    opacity: 1;
  }
  &.mission-enrollment-button-transitions-exit {
    opacity: 0;
    pointer-events: none;
  }

  ${({ $shouldTransition }) =>
    $shouldTransition
      ? css`
          transition: opacity ${ENROLL_BUTTON_TRANSITION_DURATION * 0.25}ms ease-in-out;
          transition-delay: ${ENROLL_BUTTON_TRANSITION_DURATION * 0.75}ms;
        `
      : undefined}

  ${({ $missionStatus, $show }) => css`
    ${$missionStatus === MissionCardComplexeStatuses.WON
      ? css`
          opacity: 0;
        `
      : undefined}
    ${$missionStatus === MissionCardComplexeStatuses.EXPIRED ||
    $missionStatus === MissionCardComplexeStatuses.EXPIRED_BUT_PENDING_BETS
      ? css`
          pointer-events: none;
        `
      : undefined}
      ${$show
      ? css`
          opacity: 1;
        `
      : css`
          opacity: 0;
          pointer-events: none;
        `}
  `}
`;

type StyledMissionEnrollmentButtonContainerType = {
  $large: boolean;
  $missionStatus: MissionCardComplexeStatus;
  $hide: boolean;
};

const StyledMissionEnrollmentButtonContainer = styled(
  MissionEnrollmentButtonContainer,
)<StyledMissionEnrollmentButtonContainerType>`
  position: relative;
  transition: opacity ${CTA_TRANSITION_DURATION}ms ease-in-out;
  min-width: 8em;

  ${({ $large }) =>
    $large
      ? css`
          height: 2.33em;

          .${MissionEnrollmentButtonLabelClassName} {
            font-size: 120%;
          }
        `
      : css`
          height: 1.6em;
        `}

  ${({ $missionStatus, $hide }) => css`
    ${$hide
      ? css`
          opacity: 0;
        `
      : undefined}
    ${$missionStatus !== MissionCardComplexeStatuses.EXPIRED &&
    $missionStatus !== MissionCardComplexeStatuses.EXPIRED_BUT_PENDING_BETS
      ? css`
          transform: translateZ(100px);
        `
      : undefined}
  `}
`;

const PROGRESS_BAR_TRANSITION_DURATION = CTA_TRANSITION_DURATION * 0.15;

type StyledMissionProgressBarContainerType = {
  $large: boolean;
  $missionStatus: MissionCardComplexeStatus;
  $show: boolean;
};

const StyledMissionProgressBarContainer = styled(MissionProgressBarContainer)<StyledMissionProgressBarContainerType>`
  position: absolute;

  ${({ $large }) =>
    $large
      ? css`
          height: 2.33em;
          width: 100%;

          .${MissionProgressLabelClassName} {
            font-size: 120%;
          }
        `
      : css`
          height: 1.6em;
          width: 8em;
        `}

  opacity: 0;

  &.mission-progress-bar-transitions-enter {
    opacity: 1;
  }

  &.mission-progress-bar-transitions-exit {
    opacity: 0;
    pointer-events: none;
  }

  ${({ shouldTransition }) =>
    shouldTransition
      ? css`
          transition: opacity ${PROGRESS_BAR_TRANSITION_DURATION}ms ease-in-out;
          transition-delay: ${ENROLL_BUTTON_TRANSITION_DURATION}ms;
        `
      : undefined}

  ${({ $missionStatus, $show }) => css`
    ${$show
      ? css`
          opacity: 1;
        `
      : css`
          opacity: 0;
          pointer-events: none;
        `};
    ${$missionStatus !== MissionCardComplexeStatuses.EXPIRED
      ? css`
          transform: translateZ(100px);
        `
      : undefined}
  `}
`;

type MissionCardCTAPropsType = {
  className?: string;
  missionId: number;
  missionStatus: MissionCardComplexeStatus;
  operationType?: MissionOperationType;
  isUserConnected: boolean;
  optInRequired: boolean;
  hasUserRegistered: boolean;
  shouldTransition: boolean;
  large?: boolean;
};

const MissionCardCTA = ({
  className = '',
  missionId,
  missionStatus,
  operationType,
  isUserConnected,
  optInRequired,
  hasUserRegistered,
  shouldTransition,
  large = false,
}: MissionCardCTAPropsType) => {
  const shouldRenderEnrollmentButton = useMemo(
    () =>
      !isUserConnected ||
      operationType === MissionOperationTypes.OTHER ||
      (operationType === MissionOperationTypes.MISSION &&
        ((optInRequired && !hasUserRegistered) || missionStatus === MissionCardComplexeStatuses.OPEN)),
    [isUserConnected, missionStatus, operationType, optInRequired, hasUserRegistered],
  );

  const hideEnrollmentButton = useMemo(
    () =>
      missionStatus === MissionCardComplexeStatuses.EXPIRED ||
      missionStatus === MissionCardComplexeStatuses.EXPIRED_BUT_PENDING_BETS,
    [missionStatus],
  );

  return (
    <Wrapper className={className} $large={large}>
      <CSSTransition
        in={shouldRenderEnrollmentButton}
        timeout={shouldTransition ? CTA_TRANSITION_DURATION : 1}
        classNames="mission-enrollment-button-transitions"
      >
        <EnrollmentButtonWrapper
          $missionStatus={missionStatus}
          $show={shouldRenderEnrollmentButton}
          $shouldTransition={shouldTransition}
        >
          <StyledMissionEnrollmentButtonContainer
            missionId={missionId}
            isWon={missionStatus === MissionCardComplexeStatuses.WON}
            $missionStatus={missionStatus}
            $hide={hideEnrollmentButton}
            $large={large}
          />
        </EnrollmentButtonWrapper>
      </CSSTransition>
      <CSSTransition
        in={!shouldRenderEnrollmentButton}
        timeout={shouldTransition ? CTA_TRANSITION_DURATION : 1}
        classNames="mission-progress-bar-transitions"
      >
        <StyledMissionProgressBarContainer
          missionId={missionId}
          $missionStatus={missionStatus}
          $show={!shouldRenderEnrollmentButton}
          shouldTransition={shouldTransition}
          $large={large}
        />
      </CSSTransition>
    </Wrapper>
  );
};

export default MissionCardCTA;
